import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { 
  ShoppingCart, 
  Heart, 
  ChevronLeft, 
  ChevronRight,
  Minus,
  Plus
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { productsAPI } from '../services/api';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  // Fetch product data
  const { data: productData, isLoading, error } = useQuery(
    ['product', id],
    async () => {
      const response = await productsAPI.getById(id);
      return response.data.product;
    },
    {
      enabled: !!id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  const product = productData;

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  const handleToggleWishlist = async () => {
    if (!product) return;
    // If not authenticated, reuse cart auth prompt by attempting to add to cart (it will only open the modal)
    const result = await addToCart(product, 1);
    if (result && result.success === false && result.reason === 'auth_required') {
      return;
    }
    await toggleWishlist(product.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };


  const nextImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImageIndex((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Produit non trouvé</h1>
          <p className="text-gray-300 mb-8">Le produit que vous recherchez n'existe pas.</p>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors duration-300"
          >
            <ChevronLeft size={18} />
            <span>Retour à l'accueil</span>
          </Link>
        </div>
      </div>
    );
  }

  const images = (product?.images && Array.isArray(product.images)) ? product.images : 
                 (product?.main_photo ? [product.main_photo] : []);
  const hasMultipleImages = images.length > 1;



  return (
    <div className="min-h-screen bg-gray-900 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-gray-400 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="hover:text-yellow-500 transition-colors flex items-center space-x-2"
          >
            <ChevronLeft size={16} />
            <span>Retour</span>
          </button>
          <span>/</span>
          <Link to="/categories" className="hover:text-yellow-500 transition-colors">
            Catégories
          </Link>
          {product.category_name && (
            <>
              <span>/</span>
              <span className="text-white">{product.category_name}</span>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-6">
            {/* Main Image */}
            <div className="relative aspect-square bg-gray-800 rounded-2xl overflow-hidden">
              <img
                src={images[selectedImageIndex]?.path || images[selectedImageIndex] || product?.main_photo || ''}
                alt={product?.name_fr || 'Product image'}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-gray-900/80 hover:bg-gray-900 text-white p-3 rounded-full transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-900/80 hover:bg-gray-900 text-white p-3 rounded-full transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              {/* Badges - Tous alignés à gauche, en haut, un par ligne */}
              <div className="absolute top-4 left-4 space-y-2">
                {/* Produit en vedette - Priorité haute */}
                {product.is_featured === 1 && (
                  <div className="bg-yellow-500 text-gray-900 text-sm font-bold px-4 py-2 rounded-full shadow-lg min-w-[80px] text-center">
                    ⭐ VEDETTE
                </div>
                )}
                
                {/* Nouveau produit */}
                {product.is_new === 1 && (
                  <div className="bg-green-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg min-w-[80px] text-center">
                    NOUVEAU
                </div>
                )}
                
                {/* Badge de réduction */}
                {(() => {
                  // Calculer la réduction basée sur le prix actuel vs prix original
                  // Si sale_price n'est pas défini, utiliser le prix actuel comme prix de vente
                  const currentPrice = product.sale_price || product.price;
                  const originalPrice = product.price;
                  
                  if (currentPrice < originalPrice) {
                    const discountPercentage = Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
                    if (discountPercentage > 0) {
                      return (
                        <div className="bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg min-w-[80px] text-center">
                          -{discountPercentage}%
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>

              {/* Image Counter */}
              {hasMultipleImages && (
                <div className="absolute bottom-4 right-4 bg-gray-900/80 text-white text-sm px-3 py-1 rounded-full">
                  {selectedImageIndex + 1} / {images.length}
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {hasMultipleImages && (
              <div className="grid grid-cols-5 gap-3">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square bg-gray-800 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index 
                        ? 'border-yellow-500 scale-105' 
                        : 'border-transparent hover:border-gray-600'
                    }`}
                  >
                    <img
                      src={image.path || image}
                      alt={`${product.name_fr} - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <span className="text-yellow-500 text-sm font-medium">
                  {product.category_name || 'Collection Perse'}
                </span>
                {product.sku && (
                  <span className="text-gray-400 text-sm">SKU: {product.sku}</span>
                )}
              </div>
              
              <h1 className="text-xl text-white mb-4 break-words">
                {product.name_fr}
              </h1>
              
            </div>

            {/* Price */}
            <div className="space-y-2">
              {(() => {
                const currentPrice = product.sale_price || product.price;
                const originalPrice = product.price;
                
                if (currentPrice < originalPrice) {
                  return (
                    <div className="flex items-center space-x-4">
                      <span className="text-3xl font-bold text-yellow-500">
                        {formatPrice(currentPrice)}
                      </span>
                      <span className="text-xl text-gray-400 line-through">
                        {formatPrice(originalPrice)}
                      </span>
                    </div>
                  );
                } else {
                  return (
                    <span className="text-3xl font-bold text-yellow-500">
                      {formatPrice(originalPrice)}
                    </span>
                  );
                }
              })()}
            </div>

            {/* Description */}
            {product.description_fr && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
                <p className="text-gray-300 leading-relaxed break-words whitespace-pre-line">
                  {product.description_fr}
                </p>
              </div>
            )}

            {/* Product Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {product.material && (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 text-yellow-500 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Matière</p>
                    <p className="text-white font-medium">{product.material}</p>
                  </div>
                </div>
              )}
              
              {product.dimensions && (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 text-yellow-500 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Longueur total</p>
                    <p className="text-white font-medium">{product.dimensions}</p>
                  </div>
                </div>
              )}
              
              {product.gemstone && (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 text-yellow-500 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Pierre précieuse</p>
                    <p className="text-white font-medium">{product.gemstone}</p>
                  </div>
                </div>
              )}
              
              {product.weight && (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 text-yellow-500 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Poids</p>
                    <p className="text-white font-medium">{product.weight}g</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 text-yellow-500 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Ajouté le</p>
                  <p className="text-white font-medium">
                    {new Date(product.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>

            {/* Stock Status */}
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                product.in_stock > 0 ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span className={`text-sm ${
                product.in_stock > 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {product.in_stock > 0 
                  ? `${product.in_stock} en stock` 
                  : 'Rupture de stock'
                }
              </span>
            </div>

            {/* Add to Cart Section */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="text-white font-medium">Quantité:</label>
                <div className="flex items-center border border-gray-600 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="px-3 py-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="px-4 py-2 text-white border-x border-gray-600">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(product.in_stock || 1, quantity + 1))}
                    disabled={quantity >= (product.in_stock || 1)}
                    className="px-3 py-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleAddToCart}
                  disabled={product.in_stock === 0}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <ShoppingCart size={20} />
                  <span>Ajouter au panier</span>
                </button>
                
                <button 
                  onClick={handleToggleWishlist}
                  className={`p-4 rounded-lg transition-colors ${
                    isInWishlist(product?.id)
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-white'
                  }`}
                  title={
                    isInWishlist(product?.id)
                      ? 'Retirer de la liste de souhaits'
                      : 'Ajouter à la liste de souhaits'
                  }
                >
                  <Heart 
                    size={20} 
                    className={isInWishlist(product?.id) ? 'fill-current' : ''}
                  />
                </button>
              </div>
            </div>

            
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage; 